import * as THREE from 'three';

/**
 * A drifting cumulus layer. Render-only: clouds are weather-shaped, not
 * weather. Nothing in the world may read them.
 *
 * ## The measurement this file was rebuilt around
 *
 * Every cloud used to be a horizontal pancake, and a horizontal pancake has
 * no projected area when you look at it from the side. At the altitudes this
 * game is actually played at, that is *every* cloud that is not directly
 * overhead: the elevation angle from an eye at 550 m to a cloud 10 km away is
 * two to five degrees, so a cloud's on-screen height is its width times the
 * sine of that — a few per cent of it.
 *
 * Measured on the midday gate frame, 300 m over a camp, projected corner to
 * corner: 314 x 13 px, 200 x 21, 130 x 6, and one at 34 x 0. Aspect ratios of
 * 10:1 to 24:1. Every distant cloud in the sky was a horizontal smear a
 * handful of pixels tall, and the sky band of that frame measured a standard
 * deviation of **1.6 to 7.1 luminance levels — a featureless wash**. There
 * were twenty-six clouds in the scene and the sky was empty.
 *
 * ## Why the pancake is still here
 *
 * The obvious fix — stand the quad up — is wrong, and it would have cost the
 * best thing in the frame. The sun is high, so a shadow is cast along a steep
 * ray, and a *horizontal* silhouette is exactly the right shape to cast it.
 * The moving dapple over the land is most of what makes the world read as
 * alive from god height. Standing the cloud up would have traded a real
 * ground shadow for a better sky.
 *
 * So a cloud is now two presentations of one object, crossfaded by the angle
 * it is seen from:
 *
 *   - the **slab**, horizontal, which casts the shadow and is what you see
 *     when you are under it or above it;
 *   - the **puffs**, upright and turned to face the eye, which are what you
 *     see from the side — a cumulus seen level-on is about as tall as it is
 *     wide, and that is the silhouette the sky was missing.
 *
 * The crossfade is the impostor trick and it is driven by the sine of the
 * view's elevation: straight up or down is all slab, level is all puffs, and
 * the handover happens across a wide enough band that neither pops. Total
 * presence is roughly constant from every angle, which is the property that
 * makes it read as one object rather than as two that swap.
 */
/** What the front is doing under a cloud — the same field the sim reads. */
export interface CloudSample { rain: number; storm: number; overcast: number }

export interface Clouds {
  group: THREE.Group;
  /**
   * `sample` is optional so a caller with no weather still gets the old
   * fair-weather layer. With it, a cloud standing over rain goes dark.
   */
  update(dt: number, focusX: number, focusZ: number,
         sample?: (x: number, z: number) => CloudSample): void;
}

/** A little LCG, so a cloud's shape is a pure function of its index. */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => ((a = (a * 1664525 + 1013904223) >>> 0) / 4294967296);
}

/**
 * The view from below or above: a soft irregular mass — many offset radial
 * falloffs, a few dense cores. A broken row of lobes rather than one central
 * mound, because the silhouette should read as weather, not as an egg.
 */
function cloudTexture(seed: number): THREE.CanvasTexture {
  const s = 256;
  const cv = document.createElement('canvas');
  cv.width = cv.height = s;
  const g = cv.getContext('2d')!;
  const rnd = rng(seed);
  g.clearRect(0, 0, s, s);
  const lobes = 11 + Math.floor(rnd() * 4);
  for (let i = 0; i < lobes; i++) {
    const t = i / (lobes - 1);
    const x = s * (0.18 + t * 0.64 + (rnd() - 0.5) * 0.14);
    const y = s * (0.40 + Math.sin(t * Math.PI) * 0.06 + (rnd() - 0.5) * 0.22);
    const r = s * (0.07 + rnd() * rnd() * 0.17);
    const grad = g.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, 'rgba(255,255,255,0.62)');
    grad.addColorStop(0.55, 'rgba(255,255,255,0.30)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, s, s);
  }
  // dense cores where the cloud is thickest — these carry the alphaTest
  // silhouette, so the shadow comes from the mass, not the wisps
  for (let i = 0; i < 4; i++) {
    const x = s * (0.30 + rnd() * 0.40), y = s * (0.38 + rnd() * 0.22);
    const r = s * (0.09 + rnd() * 0.10);
    const grad = g.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, 'rgba(255,255,255,0.85)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, s, s);
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/**
 * The same cloud seen from the SIDE, which is the view the sky never had.
 *
 * Two things make a cumulus recognisable in profile and neither survives
 * being drawn as a blob: the base is **flat** — it is a condensation level,
 * the altitude where the air happens to reach saturation, so it is a
 * horizontal line under the whole cloud — and the top is **lumpy**, piled
 * into rounded towers by the convection that built it. A soft oval has
 * neither and reads as smoke.
 *
 * So: lobes packed under a dome, widest at the shoulders and tapering to the
 * crown, and then everything below the condensation level is erased through a
 * short feather. The feather matters as much as the flatness — a hard cut
 * reads as a torn sticker.
 */
function cloudSideTexture(seed: number): THREE.CanvasTexture {
  const s = 256;
  const cv = document.createElement('canvas');
  cv.width = cv.height = s;
  const g = cv.getContext('2d')!;
  const rnd = rng(seed);
  g.clearRect(0, 0, s, s);
  /** The condensation level, in texture space. */
  const BASE = s * 0.80;

  const lobes = 15 + Math.floor(rnd() * 7);
  for (let i = 0; i < lobes; i++) {
    const t = i / (lobes - 1);
    // a dome profile: `spread` is 1 at the shoulders and small at the crown,
    // so the mass is broad where a cumulus is broad and narrows as it climbs
    const spread = Math.sin(Math.PI * (0.10 + t * 0.80));
    const x = s * (0.5 + (rnd() - 0.5) * 0.80 * spread);
    const rise = Math.pow(rnd(), 0.65);
    const y = BASE - rise * s * 0.56 * (0.42 + 0.58 * spread);
    const r = s * (0.08 + rnd() * rnd() * 0.15) * (0.72 + 0.55 * spread);
    const grad = g.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, 'rgba(255,255,255,0.58)');
    grad.addColorStop(0.55, 'rgba(255,255,255,0.28)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, s, s);
  }
  // the dense shoulder — a cumulus is thickest just above its base, which is
  // why the underside is the part that goes grey first
  for (let i = 0; i < 5; i++) {
    const x = s * (0.26 + rnd() * 0.48);
    const y = BASE - s * (0.04 + rnd() * 0.20);
    const r = s * (0.08 + rnd() * 0.11);
    const grad = g.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, 'rgba(255,255,255,0.82)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, s, s);
  }

  // flatten the base: erase downward through a short feather, so the bottom
  // is a soft horizontal edge rather than a cut or a rounded blob
  g.globalCompositeOperation = 'destination-out';
  const cut = g.createLinearGradient(0, BASE - s * 0.055, 0, BASE + s * 0.055);
  cut.addColorStop(0, 'rgba(0,0,0,0)');
  cut.addColorStop(1, 'rgba(0,0,0,1)');
  g.fillStyle = cut;
  g.fillRect(0, BASE - s * 0.055, s, s - BASE + s * 0.055);
  g.globalCompositeOperation = 'source-over';

  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/**
 * Clouds live in a box this far around the focus.
 *
 * Shrunk from 12,000 with the count raised, and both for one measured reason:
 * the layer was uniformly scattered over a 24 km box, so the expected number
 * of clouds within 3 km of the eye was **1.3**, and within 6 km, 5.3. A sky
 * with one cloud in it at a readable distance is an empty sky no matter how
 * well that cloud is drawn — and the far ones are exactly the ones the air
 * takes. At 48 in a 20 km box those become **3.4 and 13.6**.
 */
const WRAP = 10000;
/** How tall a cloud stands, as a fraction of how wide it is. */
const TOWER = 0.62;
/** Puffs per cloud — enough to be lumpy in profile, few enough to be cheap. */
const PUFFS = 3;

export function createClouds(count = 48): Clouds {
  const group = new THREE.Group();
  group.name = 'clouds';
  // the slab lies down; the puffs stand up. Two geometries, shared by all.
  const slabGeo = new THREE.PlaneGeometry(1, 1);
  slabGeo.rotateX(-Math.PI / 2);
  const puffGeo = new THREE.PlaneGeometry(1, 1);
  // the puff's origin is its BASE, so a cloud grows upward from its
  // condensation level instead of straddling it
  puffGeo.translate(0, 0.5, 0);

  const drift = new THREE.Vector2(5.2, 1.6);   // m/s, a lazy westerly
  /** Fair weather, and the bruised blue-grey a raining cloud wears. */
  const FAIR = new THREE.Color(0xf4f7fa), WET = new THREE.Color(0x5d6572);
  const tint = new THREE.Color();
  const wp = new THREE.Vector3();

  interface Cloud {
    root: THREE.Group;
    slab: THREE.Mesh; cap: THREE.Mesh; puffs: THREE.Mesh[];
    giant: boolean;
    /** Opacity from the weather alone; the view angle multiplies it. */
    slabWeather: number; puffWeather: number;
    vx: number; vz: number;
  }
  const clouds: Cloud[] = [];

  const h = (i: number, n: number) => {
    const x = Math.sin(i * 127.1 + n * 311.7) * 43758.5453;
    return x - Math.floor(x);
  };

  /**
   * How much of each presentation the eye should be getting, from where it
   * is. `s` is |sin(elevation)| — 0 looking level at the cloud, 1 straight
   * up or down at it.
   *
   * The bands overlap on purpose. If the handover were a single threshold a
   * cloud would visibly swap costume as you climbed past it; across a wide
   * band the two are simply both partly there, which is also the honest
   * answer, since from 40 degrees you really do see both the side and the
   * underside of a cumulus.
   */
  const smooth = (e0: number, e1: number, x: number) => {
    const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
    return t * t * (3 - 2 * t);
  };

  for (let i = 0; i < count; i++) {
    // every fifth cloud is a giant — a weather system, not a puff
    const giant = i % 5 === 0;
    const tex = cloudTexture(1 + i * 977);
    const sideTex = cloudSideTexture(7 + i * 1361);

    const root = new THREE.Group();
    const scale = giant ? 2400 + h(i, 1) * 2200 : 380 + h(i, 1) * h(i, 1) * 1400;
    const sx = scale * (0.9 + h(i, 2) * 0.5);
    const sz = scale * (giant ? 0.55 : 0.72);
    // altitude scattered through the whole layer, giants riding higher
    root.position.set(h(i, 4) * WRAP * 2 - WRAP,
                      480 + h(i, 3) * 1450 + (giant ? 350 : 0),
                      h(i, 5) * WRAP * 2 - WRAP);

    // lit, not basic: clouds take the sun and the sky like everything else,
    // so they dim honestly through dusk instead of hanging full-white in a
    // dark sky. The slight cool white keeps them from blowing out at noon.
    const slabMat = new THREE.MeshLambertMaterial({
      map: tex, color: 0xf4f7fa, transparent: true, depthWrite: false,
      opacity: giant ? 0.74 : 0.82, side: THREE.DoubleSide, fog: true,
      // giants shadow only their dense core, or one cloud eats a whole valley
      alphaTest: giant ? 0.34 : 0.22,
    });
    const slab = new THREE.Mesh(slabGeo, slabMat);
    slab.scale.set(sx, 1, sz);
    slab.castShadow = true;    // alphaTest carves the blob out of the shadow map
    root.add(slab);

    // a smaller cap riding above the pancake: from an oblique eye the pair
    // parallaxes into a hint of volume. Render-only dressing — no shadow.
    // local y is unscaled (parent scale.y = 1); no rotation — a turned child
    // under the parent's non-uniform scale would shear the quad
    const capMat = slabMat.clone();
    capMat.opacity = slabMat.opacity * 0.62;
    const cap = new THREE.Mesh(slabGeo, capMat);
    cap.position.set((h(i, 8) - 0.5) * 0.16, giant ? 130 : 60, (h(i, 9) - 0.5) * 0.12);
    cap.scale.setScalar(0.62);
    cap.castShadow = false;
    slab.add(cap);

    /*
     * The crossfade, applied per frame on whichever mesh is about to draw.
     * Both presentations read the same `|sin(elevation)|`, so they hand over
     * to each other rather than each deciding independently.
     */
    const angleFade = (cam: THREE.Camera) => {
      root.getWorldPosition(wp);
      const dy = cam.position.y - wp.y;
      const dx = cam.position.x - wp.x, dz = cam.position.z - wp.z;
      const d = Math.hypot(dx, dy, dz);
      return d < 1 ? 1 : Math.abs(dy) / d;
    };
    const c: Cloud = {
      root, slab, cap, puffs: [], giant,
      slabWeather: slabMat.opacity, puffWeather: 0.86,
      vx: drift.x * (0.6 + h(i, 6) * 0.7) * (giant ? 0.55 : 1),
      vz: drift.y * (0.6 + h(i, 7) * 0.8) * (giant ? 0.55 : 1),
    };

    // …and the side. Puffs hang off `root`, never off `slab`, because the
    // slab carries a non-uniform scale and a rotated child under one shears.
    const height = scale * TOWER * (giant ? 0.72 : 1);
    for (let k = 0; k < PUFFS; k++) {
      const puffMat = new THREE.MeshLambertMaterial({
        map: sideTex, color: 0xf4f7fa, transparent: true, depthWrite: false,
        opacity: 0.0, side: THREE.DoubleSide, fog: true,
      });
      const puff = new THREE.Mesh(puffGeo, puffMat);
      const w = scale * (0.52 + h(i * 31 + k, 11) * 0.42);
      puff.scale.set(w, height * (0.62 + h(i * 31 + k, 12) * 0.52), 1);
      // spread the towers across the cloud's own footprint, and drop them a
      // little below the slab so the flat bases line up with it rather than
      // floating above the shadow that is supposed to belong to them
      puff.position.set((h(i * 31 + k, 13) - 0.5) * sx * 0.66,
                        -height * 0.02,
                        (h(i * 31 + k, 14) - 0.5) * sz * 0.66);
      puff.castShadow = false;
      puff.renderOrder = 1;
      /*
       * Turned to face the eye, every frame, in `onBeforeRender`.
       *
       * This is deliberately NOT done in `update()`: update is handed the
       * focus point rather than the camera, and taking the camera would mean
       * changing a signature `main.ts` calls — another lane's file. three
       * hands the camera to `onBeforeRender` and recomputes `modelViewMatrix`
       * from `matrixWorld` immediately afterwards, so writing the matrix here
       * lands in the same frame.
       *
       * Yaw only. A full billboard would tip the cloud's flat base as the eye
       * rose, which is the one line in the silhouette that has to stay level.
       */
      puff.onBeforeRender = (_r, _s, cam) => {
        root.getWorldPosition(wp);
        puff.rotation.y = Math.atan2(cam.position.x - wp.x, cam.position.z - wp.z);
        puff.updateMatrixWorld();
        // …and the puffs own the level views, which is where the sky is
        puffMat.opacity = c.puffWeather * (1 - smooth(0.34, 0.86, angleFade(cam)));
      };
      c.puffs.push(puff);
      root.add(puff);
    }

    slab.onBeforeRender = (_r, _s, cam) => {
      const e = angleFade(cam);
      // the slab owns the vertical views and fades out toward level
      const k = 0.18 + 0.82 * smooth(0.06, 0.42, e);
      slabMat.opacity = c.slabWeather * k;
      capMat.opacity = c.slabWeather * 0.62 * k;
    };

    clouds.push(c);
    group.add(root);
  }

  return {
    group,
    update(dt, focusX, focusZ, sample) {
      for (const c of clouds) {
        const p = c.root.position;
        p.x += c.vx * dt;
        p.z += c.vz * dt;
        // wrap within the box around the focus, so travel never outruns the sky
        if (p.x - focusX > WRAP) p.x -= WRAP * 2;
        if (p.x - focusX < -WRAP) p.x += WRAP * 2;
        if (p.z - focusZ > WRAP) p.z -= WRAP * 2;
        if (p.z - focusZ < -WRAP) p.z += WRAP * 2;

        // A cloud standing over rain is a DARK cloud. Until this, the layer
        // was hash-placed and weather-blind — `render/curtains.ts` would hang
        // a rain shaft under a cheerful white cumulus, which reads as a
        // lighting bug rather than as a storm. The clouds still do not decide
        // anything (law 1: nothing in the world may read them); they are only
        // told what the front under them is already doing.
        if (!sample) continue;
        const w = sample(p.x, p.z);
        const wet = Math.min(1, w.rain * 0.85 + w.storm * 0.6);
        const slabMat = c.slab.material as THREE.MeshLambertMaterial;
        const capMat = c.cap.material as THREE.MeshLambertMaterial;
        tint.copy(FAIR).lerp(WET, wet);
        slabMat.color.copy(tint);
        // the cap is the cloud's own sunlit top, so it darkens LESS than the
        // base does — which is most of what makes a thunderhead read as tall
        capMat.color.copy(FAIR).lerp(WET, wet * 0.45);
        // and a raining cloud is a thicker one
        const base = c.giant ? 0.74 : 0.82;
        c.slabWeather = base * (1 + wet * 0.22);
        c.puffWeather = 0.86 * (1 + wet * 0.16);
        /*
         * The tower is where a storm actually shows. Seen from the side a wet
         * cumulus is dark along its base and shoulders and still bright at
         * the crown, because the sun is landing on the top of it — the same
         * reason the cap darkens less than the slab, one axis over. Grading
         * the puffs from base to crown is what makes a thunderhead read as
         * TALL rather than as a big grey smudge.
         */
        for (let k = 0; k < c.puffs.length; k++) {
          const m = c.puffs[k].material as THREE.MeshLambertMaterial;
          // the shortest tower is the most shadowed; the tallest catches the sun
          const crown = k / Math.max(1, c.puffs.length - 1);
          m.color.copy(FAIR).lerp(WET, wet * (0.95 - crown * 0.45));
        }
      }
    },
  };
}
